/**
 * Browser LocalStorage service to replace Cloudflare KV
 * Provides key-value storage with expiration, size management, and type safety
 */

export interface StorageItem<T = unknown> {
  value: T;
  expiresAt?: number;
  createdAt: number;
  size: number;
}

export interface StorageOptions {
  expiresIn?: number; // milliseconds
  namespace?: string;
}

export class BrowserLocalStorage {
  private readonly maxSize = 5 * 1024 * 1024; // 5MB limit for localStorage
  private readonly namespace: string;

  constructor(namespace = 'app-builder') {
    this.namespace = namespace;
    this.cleanup();
  }

  /**
   * Store a value with optional expiration
   */
  async put<T>(key: string, value: T, options: StorageOptions = {}): Promise<void> {
    const namespacedKey = this.getNamespacedKey(key);
    const serialized = JSON.stringify(value);
    const size = new Blob([serialized]).size;

    // Check if we have enough space
    await this.ensureSpace(size);

    const item: StorageItem<T> = {
      value,
      createdAt: Date.now(),
      size,
      ...(options.expiresIn && { expiresAt: Date.now() + options.expiresIn })
    };

    try {
      window.localStorage.setItem(namespacedKey, JSON.stringify(item));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        // Try to free up space and retry
        await this.cleanup(true);
        window.localStorage.setItem(namespacedKey, JSON.stringify(item));
      } else {
        throw error;
      }
    }
  }

  /**
   * Retrieve a value by key
   */
  async get<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    const namespacedKey = this.getNamespacedKey(key);
    const stored = window.localStorage.getItem(namespacedKey);

    if (!stored) {
      return defaultValue;
    }

    try {
      const item: StorageItem<T> = JSON.parse(stored);

      // Check if expired
      if (item.expiresAt && Date.now() > item.expiresAt) {
        window.localStorage.removeItem(namespacedKey);
        return defaultValue;
      }

      return item.value;
    } catch (error) {
      console.warn(`Failed to parse stored value for key ${key}:`, error);
      window.localStorage.removeItem(namespacedKey);
      return defaultValue;
    }
  }

  /**
   * Delete a value by key
   */
  async delete(key: string): Promise<void> {
    const namespacedKey = this.getNamespacedKey(key);
    window.localStorage.removeItem(namespacedKey);
  }

  /**
   * Check if a key exists and is not expired
   */
  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return value !== undefined;
  }

  /**
   * List all keys in the namespace
   */
  async list(prefix = ''): Promise<string[]> {
    const keys: string[] = [];
    const fullPrefix = this.getNamespacedKey(prefix);

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(fullPrefix)) {
        const originalKey = key.replace(this.namespace + ':', '');
        // Check if the item is not expired
        if (await this.has(originalKey)) {
          keys.push(originalKey);
        }
      }
    }

    return keys.sort();
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<{
    totalKeys: number;
    totalSize: number;
    availableSpace: number;
    namespaceKeys: number;
    namespaceSize: number;
  }> {
    let totalSize = 0;
    let namespaceKeys = 0;
    let namespaceSize = 0;

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key) {
        const value = window.localStorage.getItem(key);
        if (value) {
          const size = new Blob([value]).size;
          totalSize += size;

          if (key.startsWith(this.namespace + ':')) {
            namespaceKeys++;
            namespaceSize += size;
          }
        }
      }
    }

    return {
      totalKeys: window.localStorage.length,
      totalSize,
      availableSpace: this.maxSize - totalSize,
      namespaceKeys,
      namespaceSize
    };
  }

  /**
   * Clear all data in the namespace
   */
  async clear(): Promise<void> {
    const keys = await this.list();
    for (const key of keys) {
      await this.delete(key);
    }
  }

  /**
   * Export all data in the namespace
   */
  async export(): Promise<Record<string, unknown>> {
    const data: Record<string, unknown> = {};
    const keys = await this.list();

    for (const key of keys) {
      data[key] = await this.get(key);
    }

    return data;
  }

  /**
   * Import data into the namespace
   */
  async import(data: Record<string, unknown>, options: StorageOptions = {}): Promise<void> {
    for (const [key, value] of Object.entries(data)) {
      await this.put(key, value, options);
    }
  }

  /**
   * Batch operations for better performance
   */
  async batch(operations: Array<{
    type: 'put' | 'delete';
    key: string;
    value?: unknown;
    options?: StorageOptions;
  }>): Promise<void> {
    for (const op of operations) {
      if (op.type === 'put' && op.value !== undefined) {
        await this.put(op.key, op.value, op.options);
      } else if (op.type === 'delete') {
        await this.delete(op.key);
      }
    }
  }

  /**
   * Get namespaced key
   */
  private getNamespacedKey(key: string): string {
    return `${this.namespace}:${key}`;
  }

  /**
   * Cleanup expired items and optionally force cleanup for space
   */
  private async cleanup(force = false): Promise<void> {
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(this.namespace + ':')) {
        const stored = window.localStorage.getItem(key);
        if (stored) {
          try {
            const item: StorageItem = JSON.parse(stored);

            // Remove expired items
            if (item.expiresAt && now > item.expiresAt) {
              keysToRemove.push(key);
            }
            // If force cleanup, remove oldest items
            else if (force && item.createdAt < now - (7 * 24 * 60 * 60 * 1000)) { // 7 days old
              keysToRemove.push(key);
            }
          } catch (error) {
            // Remove corrupted items
            keysToRemove.push(key);
          }
        }
      }
    }

    // Remove identified keys
    for (const key of keysToRemove) {
      window.localStorage.removeItem(key);
    }
  }

  /**
   * Ensure we have enough space for a new item
   */
  private async ensureSpace(requiredSize: number): Promise<void> {
    const stats = await this.getStats();
    
    if (stats.availableSpace < requiredSize) {
      // Try cleanup first
      await this.cleanup(true);
      
      const newStats = await this.getStats();
      if (newStats.availableSpace < requiredSize) {
        throw new Error(`Not enough storage space. Required: ${requiredSize}, Available: ${newStats.availableSpace}`);
      }
    }
  }
}

// Specialized storage instances for different use cases
export const userPreferencesStorage = new BrowserLocalStorage('app-builder:preferences');
export const uiStateStorage = new BrowserLocalStorage('app-builder:ui-state');
export const cacheStorage = new BrowserLocalStorage('app-builder:cache');
export const sessionStorage = new BrowserLocalStorage('app-builder:session');

// Default storage instance
export const localStorage = new BrowserLocalStorage();

// Utility functions for common operations
export const storageUtils = {
  /**
   * Store user preference with automatic expiration
   */
  async setUserPreference(key: string, value: unknown): Promise<void> {
    await userPreferencesStorage.put(key, value, { expiresIn: 30 * 24 * 60 * 60 * 1000 }); // 30 days
  },

  /**
   * Get user preference
   */
  async getUserPreference<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    return await userPreferencesStorage.get(key, defaultValue);
  },

  /**
   * Store UI state (temporary)
   */
  async setUIState(key: string, value: unknown): Promise<void> {
    await uiStateStorage.put(key, value, { expiresIn: 24 * 60 * 60 * 1000 }); // 1 day
  },

  /**
   * Get UI state
   */
  async getUIState<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    return await uiStateStorage.get(key, defaultValue);
  },

  /**
   * Cache data with expiration
   */
  async cache(key: string, value: unknown, expiresIn = 60 * 60 * 1000): Promise<void> { // 1 hour default
    await cacheStorage.put(key, value, { expiresIn });
  },

  /**
   * Get cached data
   */
  async getCached<T>(key: string): Promise<T | undefined> {
    return await cacheStorage.get(key);
  },

  /**
   * Clear all storage
   */
  async clearAll(): Promise<void> {
    await Promise.all([
      userPreferencesStorage.clear(),
      uiStateStorage.clear(),
      cacheStorage.clear(),
      sessionStorage.clear(),
      localStorage.clear()
    ]);
  },

  /**
   * Get total storage usage
   */
  async getTotalUsage(): Promise<{
    preferences: number;
    uiState: number;
    cache: number;
    session: number;
    general: number;
    total: number;
  }> {
    const [prefStats, uiStats, cacheStats, sessionStats, generalStats] = await Promise.all([
      userPreferencesStorage.getStats(),
      uiStateStorage.getStats(),
      cacheStorage.getStats(),
      sessionStorage.getStats(),
      localStorage.getStats()
    ]);

    return {
      preferences: prefStats.namespaceSize,
      uiState: uiStats.namespaceSize,
      cache: cacheStats.namespaceSize,
      session: sessionStats.namespaceSize,
      general: generalStats.namespaceSize,
      total: prefStats.namespaceSize + uiStats.namespaceSize + cacheStats.namespaceSize + 
             sessionStats.namespaceSize + generalStats.namespaceSize
    };
  }
};
